import { useEffect, useMemo, useState } from "react";
import { updateOnboardingAll } from "../utils/api.js";
import { loadActiveSiteNames, peekActiveSiteNames } from "../utils/sitesCache.js";
import CustomSelect from "./shared/CustomSelect";
import {
  FIELD_LABELS,
  DATE_FIELDS,
  EDITABLE_FIELDS,
  NEW_COMPLETED_FIELDS,
  NEW_COMPLETED_OPTIONS,
  POSITION_OPTIONS,
  EMPLOYMENT_VISIBILITY,
  TOP_FIELD_ORDER,
  SUBMIT_KEYS,
  formatMobile,
  typeOptionsForStatus,
  validateSubmission,
} from "../utils/onboardingFields.js";

/* ============================================================================
 * OnboardingEditForm — shared modal editor
 * ----------------------------------------------------------------------------
 * Renders the onboarding record editor inside a modal. Used by
 * OnboardingAllUsers (record button) and OnboardActive (row click).
 *
 * Props:
 *   record       – the record object to edit (must include recordId + fields)
 *   userRecords  – the same person's sibling records (for the conversion guard)
 *   onClose()    – close the modal without saving
 *   onSaved(payload) – called after a successful save with the merged payload;
 *                      the host merges it into its local records + shows a toast
 *
 * Site Working is a CustomSelect fed by the page-level sitesCache (/api/sites).
 * Active sites are offered; the record's current site is always kept in the
 * list even if it has since gone Non Active, so the value never disappears.
 * ==========================================================================*/

const GRID_3COL = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  columnGap: "20px",
  rowGap: "12px",
};


const OnboardingEditForm = ({ record, userRecords = [], onClose, onSaved }) => {
  const [formData, setFormData] = useState(() => ({ ...record }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Site list for the Site Working select — seeded synchronously from the
  // page-level cache so the select renders fully populated on first paint.
  const [activeSites, setActiveSites] = useState(() => peekActiveSiteNames() ?? []);

  useEffect(() => {
    let alive = true;
    loadActiveSiteNames().then((names) => { if (alive) setActiveSites(names); });
    return () => { alive = false; };
  }, []);

  /* active sites, A→Z, plus the current value if it isn't in the list */
  const siteOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    activeSites.forEach((n) => {
      const k = n.toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(n); }
    });
    out.sort((a, b) => a.localeCompare(b));
    const current = (formData.siteWorking ?? "").toString().trim();
    if (current && !seen.has(current.toLowerCase())) out.unshift(current);
    return out;
  }, [activeSites, formData.siteWorking]);

  // Type Of Employment options are locked to the record's Work Status.
  const typeOptions = typeOptionsForStatus(formData.workStatus);

  // Which employment-driven fields to reveal (Casual employees get a reduced set).
  const employmentBlockKeys =
    formData.typeOfEmployment === "Employee" && formData.position === "Casual"
      ? ["formComplete", "weeklyHours", "grossSalary", "superPayment"]
      : EMPLOYMENT_VISIBILITY[formData.typeOfEmployment] ?? [];

  const handleFieldChange = (key, value) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const nextVal = key === "mobile" ? formatMobile(value) : value;
      return { ...prev, [key]: nextVal };
    });
  };

  const renderField = (key) => {
    const label = FIELD_LABELS[key];
    const isDate = DATE_FIELDS.has(key);
    const value = formData[key] ?? "";

    // Work Status is read-only — it's determined by the entity type and can
    // never legally flip, so it isn't editable here.
    if (key === "workStatus") {
      return (
        <div className="ob-field" key={key}>
          <label className="ob-label">{label}</label>
          <input type="text" value={value} readOnly className="ob-input ob-input-readonly" />
        </div>
      );
    }

    if (EDITABLE_FIELDS.has(key)) {
      if (key === "typeOfEmployment") {
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={typeOptions}
            />
          </div>
        );
      }
      if (key === "position") {
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={POSITION_OPTIONS}
            />
          </div>
        );
      }
      if (key === "siteWorking") {
        // Always a CustomSelect — before the list arrives it holds just the
        // current value, so the field never changes shape.
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={siteOptions}
            />
          </div>
        );
      }
      if (NEW_COMPLETED_FIELDS.has(key)) {
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={NEW_COMPLETED_OPTIONS}
            />
          </div>
        );
      }
      return (
        <div className="ob-field" key={key}>
          <label className="ob-label">{label}</label>
          <input
            type={isDate ? "date" : "text"}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            className="ob-input"
            placeholder=""
          />
        </div>
      );
    }

    // Read-only
    return (
      <div className="ob-field" key={key}>
        <label className="ob-label">{label}</label>
        <input
          type={isDate ? "date" : "text"}
          value={value}
          readOnly
          className="ob-input ob-input-readonly"
          placeholder=""
        />
      </div>
    );
  };

  const buildSubmitPayload = () => {
    const payload = { recordId: formData.recordId };
    SUBMIT_KEYS.forEach((key) => {
      payload[key] = formData[key] ?? "";
    });
    return payload;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const payload = buildSubmitPayload();

    // Safety net — the UI can't offer an illegal flip, but guard anyway.
    const validation = validateSubmission(payload, userRecords);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await updateOnboardingAll(payload);
      if (res?.status === "success") {
        onSaved?.(payload);
        onClose?.();
      } else {
        throw new Error(res?.message || "Failed to update record");
      }
    } catch (err) {
      setError(err.message || "Failed to update record");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={submitting ? undefined : onClose}
        style={{ position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.7)", zIndex: 998 }}
      />
      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: "calc(var(--fr-modal-top, 0px) + (100dvh - var(--fr-modal-top, 0px) - 68px) / 2)",   // centred UNDER the info bar
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(1100px, 94vw)",
          maxHeight: "min(90vh, calc(100dvh - var(--fr-modal-top, 0px) - 108px))",
          overflowY: "auto",
          zIndex: 999,
        }}
      >
        <div
          className="ob-form-card"
          style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
        >
          <div className="ob-form-fields">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                {formData.workStatus} Record
              </h2>
              <button
                onClick={onClose}
                disabled={submitting}
                style={{
                  background: "none",
                  border: "none",
                  cursor: submitting ? "not-allowed" : "pointer",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>

            {error && (
              <div className="ob-info-banner ob-info-banner-warning" style={{ marginBottom: "12px" }}>
                {error}
              </div>
            )}

            {/* Top, always-visible block */}
            <div style={GRID_3COL}>{TOP_FIELD_ORDER.map(renderField)}</div>

            <div className="ob-divider" />

            {/* Employment-driven block */}
            <div style={{ color: "#22d3ee", fontSize: "13px", fontWeight: 500, marginBottom: "8px" }}>
              Section below adjusts based on Type Of Employment.
            </div>

            {employmentBlockKeys.length === 0 ? (
              <div style={{ color: "#6b7280", fontSize: "13px", padding: "8px 0" }}>
                Select a Type Of Employment to reveal the relevant fields.
              </div>
            ) : (
              <div style={GRID_3COL}>{employmentBlockKeys.map(renderField)}</div>
            )}

            <div className="ob-divider" />

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={onClose}
                disabled={submitting}
                className="ob-btn ob-btn-check"
                style={{ minWidth: "120px" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="ob-btn ob-btn-submit"
                style={{ minWidth: "120px" }}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingEditForm;